import { Navigate, useParams } from "react-router-dom";
import { detectLanguage } from "./RootRedirect.jsx";
import { events, larpPages, spendenProjects, findByLangAndId } from "../utils/content.js";
import { STATIC_PAGES } from "../utils/staticPages.js";

// events uses a literal "events" segment, identical in both languages (see
// App.jsx) — no {de,en} prefix needed for it, unlike larp-erleben/spenden.
const PATH_PREFIXES = {
  larp: { de: "larp-erleben", en: "experience-larp" },
  spenden: { de: "spenden", en: "donate" },
};

const COLLECTIONS = [
  { items: events, prefix: null },
  { items: larpPages, prefix: PATH_PREFIXES.larp },
  { items: spendenProjects, prefix: PATH_PREFIXES.spenden },
];

/**
 * Resolves a page id — either a static page's id (staticPages.js) or a
 * content-item id (the one field shared between a DE/EN pair, slugs are
 * independent per language, see content.js) — plus a detected language, to
 * that page's real localized path. Returns null if nothing matches. Kept as
 * a plain function, not inlined in the component, so it's unit-testable
 * without component-rendering test infra — this project has no
 * @testing-library/react at the root, only in dashboard/.
 */
export function resolveContentPath(id, lang) {
  const staticPage = STATIC_PAGES[id];
  if (staticPage) {
    return `/${lang}/${staticPage[lang]}`;
  }

  for (const { items, prefix } of COLLECTIONS) {
    const page = findByLangAndId(items, lang, id);
    if (page) {
      const segment = prefix ? prefix[lang] : "events";
      return `/${lang}/${segment}/${page.frontmatter.slug}`;
    }
  }
  return null;
}

/**
 * GET /go/:id — a language-neutral link target for tales.wtf short links,
 * for ANY page on the site (static pages like Impressum/Kontakt/About via
 * staticPages.js, as well as events/larp-erleben/spenden content). Lets one
 * Cosmos shortlink record (targetUrl: ".../go/{id}") serve both languages
 * instead of needing one record per language (see project notes on the
 * shortUrl-collision problem this replaces). Sits outside /:lang/* exactly
 * like RootRedirect at "/" — no SEOHead, deliberately excluded from the
 * sitemap/prerender route list (scripts/lib/routes.js), so it carries no
 * duplicate-content/canonical risk.
 */
export default function ContentRedirect() {
  const { id } = useParams();
  const lang = detectLanguage();
  const path = resolveContentPath(id, lang);
  return <Navigate to={path ?? `/${lang}/`} replace />;
}
